/**
 * Ask AI widget orchestrator — the ONLY thing the app shell imports.
 * Rendered through a portal on document.body so position:fixed is never
 * trapped by an ancestor transform/stacking context (full-height drawer).
 */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useAskAiBridge } from '@/shared/stores/ask-ai-bridge.store'
import { parsePageContext } from '../lib/page-context'
import { prefetchAskAi } from '../lib/chat-cache'
import { AskAiBubble } from './ask-ai-bubble'

const loadDrawer = () => import('./ask-ai-drawer')
const AskAiDrawer = lazy(() => loadDrawer().then((m) => ({ default: m.AskAiDrawer })))

export function AskAiWidget() {
  const { isAuthenticated } = useAuthStore()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [side, setSide] = useState<'left' | 'right'>('right')

  const handleOpen = useCallback(() => {
    // Dock the drawer on whichever side the user parked the bubble.
    try {
      const corner = localStorage.getItem('mcd-chat-corner')
      setSide(corner?.endsWith('l') ? 'left' : 'right')
    } catch {
      setSide('right')
    }
    setOpen(true)
  }, [])

  // Bridge for page-level drawers (AskAiReturnBar): they close the chat when
  // they would open underneath it, and reopen it via "Back to AI chat".
  useEffect(() => {
    const bridge = useAskAiBridge.getState()
    bridge.registerChatControls({
      openChat: handleOpen,
      closeChat: () => setOpen(false),
    })
    return () => bridge.registerChatControls({ openChat: null, closeChat: null })
  }, [handleOpen])

  useEffect(() => {
    useAskAiBridge.getState().setChatOpen(open)
  }, [open])

  // Warm everything the first open needs: the conversation list + resume
  // thread (session cache) and the drawer's lazy chunk.
  useEffect(() => {
    if (!isAuthenticated) return
    void prefetchAskAi().catch(() => undefined)
    void loadDrawer()
  }, [isAuthenticated])

  const pageContext = parsePageContext(location.pathname || '/')
  if (!isAuthenticated || !pageContext) return null

  return createPortal(
    <>
      {!open && <AskAiBubble onOpen={handleOpen} />}
      <AnimatePresence>
        {open && (
          <Suspense fallback={null}>
            <AskAiDrawer pageContext={pageContext} side={side} onClose={() => setOpen(false)} />
          </Suspense>
        )}
      </AnimatePresence>
    </>,
    document.body,
  )
}
